import { createContextualCan, useAbility as useCaslAbility } from '@casl/react';
import { createContext, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { defineAbilityFor, defineFallbackAbility } from '@arc/shared/util-auth';
import { useAuth } from './use-auth';
const AbilityContext = createContext(defineFallbackAbility());
export function AbilityProvider({
  children
}) {
  const {
    user
  } = useAuth();
  const ability = useMemo(() => defineAbilityFor(user), [user]);
  return <AbilityContext.Provider value={ability}>
			{children}
		</AbilityContext.Provider>;
}

/**
 * Returns the ability object of the current authenticated user for checking permissions
 * and a string generator for when a user is lacking a permission
 * See https://casl.js.org/v4/en/guide/intro#conditions for examples.
 * This is used instead of `@casl/react`'s useAbility because their ability requires
 * the context to be imported
 */
export function useAbility() {
  const ability = useCaslAbility(AbilityContext);
  const {
    t
  } = useTranslation('permissions');
  const missingPermissionText = useCallback((action, permission) => t('no permission', {
    action: t(`action ${action}`),
    permission: t(`type ${permission}`)
  }), [t]);
  return {
    ability,
    missingPermissionText
  };
}
export const Can = createContextualCan(AbilityContext.Consumer);